import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

import Note from '../../note';
import formFieldOperationOptions from '../../utils/formFieldOperationOptions';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import getFormattedPricing from '../../iot/orderDetailsSection/utils';
import { FieldLabelWrapper } from './css';
import FieldGuide from './common/fieldGuide';
import FieldTitle from './common/FieldTitle';

const styles = {
  control: baseStyles => ({
    ...baseStyles,
    borderColor: '#54595f'
  }),
  input: provided => ({
    ...provided,
    height: '2.3rem',
    fontSize: '12px'
  }),
  // A z-index of 1 gets trapped behind later siblings that create their own stacking
  // context - render the menu in a body-level portal so it always paints on top.
  menuPortal: provided => ({ ...provided, zIndex: 9999 })
};

const theme = options => {
  return {
    ...options,
    colors: {
      ...options.colors,
      primary25: '#b2d47e',
      primary: '#2B8000',
      primary50: '#b2d47e'
    }
  };
};

const FieldSelect = ({ data }) => {
  const {
    ref,
    name,
    label,
    value,
    placeholder,
    required,
    options,
    onChange,
    note,
    tooltip,
    tooltipLong,
    toolTipRichText,
    disabled,
    readonly,
    noteAppearsBelowField,
    hideEmptyValue,
    isSearchable,
    isShowNoteBackground,
    isLabelBold
  } = data;

  if (!options) return null;

  const renderNotes = () => {
    return (
      note &&
      note.content && (
        <Note
          type="richText"
          options={formFieldOperationOptions(value)}
          data={note}
          isShowNoteBackground={isShowNoteBackground}
        />
      )
    );
  };
  const localizedStrings = useLocalizedStringsContext();
  const { quoteForm } = localizedStrings;
  const tooltipMessage = tooltip || tooltipLong || toolTipRichText;
  const updatedOption = item => {
    if (!item.fields) return null;

    const {
      fields: {
        value: itemValue,
        label: itemLabel,
        price,
        monthlyPayment,
        price12,
        monthlyPayment12,
        monthlyPayment24,
        price36,
        annualPayment36,
        monthlyPayment36,
        showPricesInLabel,
        isVisible
      }
    } = item;

    // Hide option if isVisible is set to No
    if (typeof isVisible !== 'undefined' && !isVisible) return null;

    let optionLabel = itemLabel;
    const priceLabelArr = [];
    if (showPricesInLabel) {
      if (price)
        priceLabelArr.push(
          `${quoteForm.upfront}: ${getFormattedPricing(price)}`
        );
      if (monthlyPayment)
        priceLabelArr.push(
          `${quoteForm.monthly}: ${getFormattedPricing(monthlyPayment)}`
        );
      if (price12)
        priceLabelArr.push(
          `${quoteForm.upfront12}: ${getFormattedPricing(price12)}`
        );
      if (monthlyPayment12)
        priceLabelArr.push(
          `${quoteForm.monthly12}: ${getFormattedPricing(monthlyPayment12)}`
        );
      if (monthlyPayment24)
        priceLabelArr.push(
          `${quoteForm.monthly24}: ${getFormattedPricing(monthlyPayment24)}`
        );
      if (price36)
        priceLabelArr.push(
          `${quoteForm.upfront36}: ${getFormattedPricing(price36)}`
        );
      if (annualPayment36)
        priceLabelArr.push(
          `${quoteForm.annual36}: ${getFormattedPricing(annualPayment36)}`
        );
      if (monthlyPayment36)
        priceLabelArr.push(
          `${quoteForm.monthly36}: ${getFormattedPricing(monthlyPayment36)}`
        );
      if (priceLabelArr.length)
        optionLabel += `:   (${priceLabelArr.join(', ')})`;
    }

    return {
      label: optionLabel,
      value: itemValue,
      selected: value === itemValue
    };
  };
  const updatedOptions = options.map(updatedOption);

  return (
    <div
      className={
        ['outlet', 'channelId'].includes(name)
          ? 'outlet-field-container'
          : 'select-field-container'
      }
    >
      <FieldLabelWrapper>
        <FieldTitle required={required} label={label} isLabelBold={isLabelBold} />
        {tooltipMessage && (
          <FieldGuide tooltip={tooltipMessage} isRichText={toolTipRichText} />
        )}
      </FieldLabelWrapper>
      {!noteAppearsBelowField && renderNotes()}

      {isSearchable ? (
        <Select
          ref={ref}
          id={name}
          placeholder={placeholder}
          className="basic-single"
          classNamePrefix="select"
          styles={styles}
          theme={theme}
          isDisabled={disabled || readonly}
          isClearable={false}
          isSearchable
          name={name}
          value={value && { label: value, value }}
          onChange={e => onChange({ target: { name, value: e.value } })}
          options={updatedOptions}
          menuPortalTarget={document.body}
        />
      ) : (
        <select
          ref={ref}
          id={name}
          name={name}
          onChange={onChange}
          disabled={disabled || readonly}
          className="down-arrow"
          style={{ marginBottom: '0.5rem' }}
        >
          {!hideEmptyValue && (
            <option value="" disabled selected>
              {placeholder}
            </option>
          )}
          {updatedOptions.map(
            option =>
              option && (
                <option
                  key={`${option.label}-${option.value}`}
                  value={option.value}
                  selected={option.selected}
                >
                  {option.label}
                </option>
              )
          )}
        </select>
      )}

      {noteAppearsBelowField && renderNotes()}
    </div>
  );
};

FieldSelect.propTypes = {
  data: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    ref: PropTypes.object,
    placeholder: PropTypes.string,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    required: PropTypes.bool,
    readonly: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          value: PropTypes.string.isRequired
        })
      })
    ),
    onChange: PropTypes.func.isRequired,
    note: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    disabled: PropTypes.bool,
    tooltip: PropTypes.string,
    tooltipLong: PropTypes.string,
    toolTipRichText: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    noteAppearsBelowField: PropTypes.bool,
    hideEmptyValue: PropTypes.bool,
    isSearchable: PropTypes.bool,
    isShowNoteBackground: PropTypes.bool,
    isLabelBold: PropTypes.bool
  }).isRequired
};

export default FieldSelect;
